import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SearchableSelect } from '../components/ui/searchable-select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { AlertCircle, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FundRequestCreatePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [budgetInfo, setBudgetInfo] = useState(null);
  const [loadingBudget, setLoadingBudget] = useState(false);
  const [formData, setFormData] = useState({
    quote_id: '',
    requested_amount: '',
    remarks: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      toast.error('Failed to load quotes');
    }
  };

  const fetchBudgetInfo = async (quoteId) => {
    if (!quoteId) {
      setBudgetInfo(null);
      return;
    }
    
    setLoadingBudget(true);
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/budget`);
      setBudgetInfo(response.data);
    } catch (error) {
      toast.error('Failed to load budget information');
      setBudgetInfo(null);
    } finally {
      setLoadingBudget(false);
    }
  };

  const handleQuoteChange = (quoteId) => {
    setFormData({ ...formData, quote_id: quoteId, requested_amount: '' });
    fetchBudgetInfo(quoteId);
  };

  const validateForm = () => {
    if (!formData.quote_id) {
      toast.error('Please select a quote');
      return false;
    }
    
    const amount = parseFloat(formData.requested_amount);
    if (!amount || amount <= 0) {
      toast.error('Requested amount must be greater than zero');
      return false;
    }
    
    if (budgetInfo && amount > budgetInfo.available_budget) {
      toast.error(`Requested amount exceeds available budget of Rs.${budgetInfo.available_budget.toLocaleString('en-IN')}`);
      return false;
    }
    
    if (!formData.remarks.trim()) {
      toast.error('Please provide remarks');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API}/fund-requests`, {
        ...formData,
        requested_amount: parseFloat(formData.requested_amount)
      });
      toast.success('Fund request created successfully');
      navigate('/fund-requests');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create fund request');
    } finally {
      setLoading(false);
    }
  };

  // Prepare quote options for searchable select
  const quoteOptions = quotes.map((quote) => ({
    value: quote.id,
    label: `${quote.quote_no} - ${(quote.work_description || '').substring(0, 50)}${(quote.work_description || '').length > 50 ? '...' : ''}`,
    quote_no: quote.quote_no,
    work_description: quote.work_description
  }));

  return (
    <Sidebar>
      <div className="space-y-6 max-w-2xl" data-testid="fund-request-create-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Create Fund Request
          </h1>
          <p className="text-muted-foreground mt-2">
            Request funds for an assigned quote
          </p>
        </div>

        <Card className="p-6 glass-card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="quote">Select Quote *</Label>
              <SearchableSelect
                options={quoteOptions}
                value={formData.quote_id}
                onValueChange={handleQuoteChange}
                placeholder="Search and select a quote..."
                searchPlaceholder="Search by quote number or description..."
                emptyText="No quotes found."
                getOptionValue={(opt) => opt.value}
                getOptionLabel={(opt) => opt.label}
                data-testid="quote-select"
              />
            </div>

            {/* Budget Information Card */}
            {formData.quote_id && (
              <div className={`p-4 rounded-lg border-2 ${
                budgetInfo?.available_budget > 0 
                  ? 'bg-blue-50 border-blue-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                {loadingBudget ? (
                  <div className="flex items-center gap-2 text-gray-600">
                    <div className="h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    Loading budget info...
                  </div>
                ) : budgetInfo ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 font-medium text-gray-700">
                      <Info className="h-4 w-4" />
                      Budget Information for {budgetInfo.quote_no}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                      <div>
                        <p className="text-xs text-gray-500">Total Budget</p>
                        <p className="font-semibold text-gray-800">Rs.{budgetInfo.total_budget.toLocaleString('en-IN')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Already Approved</p>
                        <p className="font-semibold text-orange-600">Rs.{budgetInfo.total_approved.toLocaleString('en-IN')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Pending Approval</p>
                        <p className="font-semibold text-amber-600">Rs.{(budgetInfo.pending_approval || 0).toLocaleString('en-IN')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Available Budget</p>
                        <p className={`font-semibold ${budgetInfo.available_budget > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          Rs.{budgetInfo.available_budget.toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                    {budgetInfo.available_budget <= 0 && (
                      <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
                        <AlertCircle className="h-4 w-4" />
                        No budget available for this quote
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="amount">Request Amount (Rs.) *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="Enter amount"
                value={formData.requested_amount}
                onChange={(e) => setFormData({ ...formData, requested_amount: e.target.value })}
                required
                data-testid="amount-input"
                className={
                  budgetInfo && parseFloat(formData.requested_amount) > budgetInfo.available_budget
                    ? 'border-red-500 focus:border-red-500'
                    : ''
                }
              />
              {budgetInfo && parseFloat(formData.requested_amount) > budgetInfo.available_budget && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Amount exceeds available budget
                </p>
              )}
              {(formData.requested_amount === '' || parseFloat(formData.requested_amount) === 0) && formData.quote_id && (
                <p className="text-sm text-amber-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Amount must be greater than zero
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="remarks">Remarks * <span className="text-xs text-muted-foreground">({formData.remarks.length}/500)</span></Label>
              <Textarea
                id="remarks"
                placeholder="Provide details about the fund request..."
                value={formData.remarks}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, remarks: e.target.value });
                  }
                }}
                required
                maxLength={500}
                rows={4}
                data-testid="remarks-input"
              />
            </div>

            <div className="flex gap-3">
              <Button 
                type="submit" 
                disabled={
                  loading || 
                  !formData.quote_id || 
                  !formData.requested_amount ||
                  parseFloat(formData.requested_amount) <= 0 ||
                  (budgetInfo && parseFloat(formData.requested_amount) > budgetInfo.available_budget)
                } 
                data-testid="submit-button"
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/fund-requests')}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Sidebar>
  );
}
